import type { Session } from "@supabase/supabase-js";

import { requireSupabase } from "./supabase";
import { toMinor } from "./money";
import type {
  AccountBalance,
  AccountKind,
  Category,
  ExpenseCategoryYear,
  IncomeSource,
  IncomeSourceMonth,
  LedgerTransaction,
  MonthSummary,
  OperationDraft,
  WorkspaceData,
} from "./types";

const DEFAULT_CATEGORIES = [
  "Продукты",
  "Столовая и кафе",
  "Топливо",
  "Ремонт автомобиля",
  "ИИ-сервисы",
  "Маркетинг",
  "Подарки и близкие",
  "Доставка и логистика",
  "Одежда и обувь",
  "Другое",
];
const DEFAULT_SOURCES = ["Зарплата", "Оплата за работу", "Другое"];

function assertOnline(): void {
  if (!navigator.onLine) {
    throw new Error("Для изменения финансовых данных нужно подключение к интернету.");
  }
}

function throwIfError(error: { message: string; hint?: string | null; code?: string } | null): void {
  if (!error) return;
  if (error.message.includes("active historical summary")) {
    throw new Error(
      "За этот месяц уже действует историческая сводка. Чтобы добавить реальные операции, сначала явно замените сводку восстановленными данными месяца.",
    );
  }
  throw new Error(error.hint ? `${error.message}. ${error.hint}` : error.message);
}

export async function getSession(): Promise<Session | null> {
  const { data, error } = await requireSupabase().auth.getSession();
  throwIfError(error);
  return data.session;
}

export async function signIn(email: string, password: string): Promise<void> {
  assertOnline();
  const { error } = await requireSupabase().auth.signInWithPassword({ email, password });
  throwIfError(error);
}

export async function signUp(email: string, password: string): Promise<string> {
  assertOnline();
  const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone || "Europe/Moscow";
  const { data, error } = await requireSupabase().auth.signUp({
    email,
    password,
    options: { data: { timezone } },
  });
  throwIfError(error);
  return data.session ? "Аккаунт создан" : "Проверьте почту и подтвердите регистрацию";
}

export async function signOut(): Promise<void> {
  const { error } = await requireSupabase().auth.signOut();
  throwIfError(error);
}

export function onAuthChange(callback: (session: Session | null) => void): () => void {
  const { data } = requireSupabase().auth.onAuthStateChange((_event, session) => callback(session));
  return () => data.subscription.unsubscribe();
}

async function ensureDefaultDirectories(userId: string): Promise<void> {
  const client = requireSupabase();
  const [categoryResult, sourceResult] = await Promise.all([
    client.from("categories").select("id").limit(1),
    client.from("income_sources").select("id").limit(1),
  ]);
  throwIfError(categoryResult.error);
  throwIfError(sourceResult.error);

  if (!categoryResult.data?.length) {
    const { error } = await client.from("categories").insert(
      DEFAULT_CATEGORIES.map((name, index) => ({ user_id: userId, name, sort_order: index * 10 })),
    );
    if (error?.code !== "23505") throwIfError(error);
  }
  if (!sourceResult.data?.length) {
    const { error } = await client.from("income_sources").insert(
      DEFAULT_SOURCES.map((name, index) => ({ user_id: userId, name, sort_order: index * 10 })),
    );
    if (error?.code !== "23505") throwIfError(error);
  }
}

export async function loadWorkspace(userId: string): Promise<WorkspaceData> {
  await ensureDefaultDirectories(userId);
  const client = requireSupabase();
  const [accountResult, categoryResult, sourceResult, transactionResult] = await Promise.all([
    client.rpc("get_account_balances"),
    client.from("categories").select("id,name,parent_id,sort_order,archived_at").order("sort_order"),
    client.from("income_sources").select("id,name,sort_order,archived_at").order("sort_order"),
    client
      .from("transactions")
      .select(
        "id,type,amount_minor,occurred_on,category_id,income_source_id,refund_of_transaction_id,description,counterparty,original_text,deleted_at,created_at,transaction_entries(id,account_id,amount_minor)",
      )
      .order("occurred_on", { ascending: false })
      .order("created_at", { ascending: false })
      .limit(1000),
  ]);
  throwIfError(accountResult.error);
  throwIfError(categoryResult.error);
  throwIfError(sourceResult.error);
  throwIfError(transactionResult.error);

  const accounts: AccountBalance[] = (accountResult.data ?? []).map((row: Record<string, unknown>) => ({
    accountId: String(row.account_id),
    name: String(row.name),
    organizationName: row.organization_name ? String(row.organization_name) : null,
    accentColor: row.accent_color ? String(row.accent_color) : null,
    kind: row.kind as AccountKind,
    currency: "RUB",
    sortOrder: Number(row.sort_order),
    archivedAt: row.archived_at ? String(row.archived_at) : null,
    balanceMinor: toMinor(row.balance_minor as string),
  }));
  const categories: Category[] = (categoryResult.data ?? []).map((row) => ({
    id: row.id,
    name: row.name,
    parentId: row.parent_id,
    sortOrder: row.sort_order,
    archivedAt: row.archived_at,
  }));
  const incomeSources: IncomeSource[] = (sourceResult.data ?? []).map((row) => ({
    id: row.id,
    name: row.name,
    sortOrder: row.sort_order,
    archivedAt: row.archived_at,
  }));
  const transactions: LedgerTransaction[] = (transactionResult.data ?? []).map((row) => ({
    id: row.id,
    type: row.type,
    amountMinor: toMinor(row.amount_minor),
    occurredOn: row.occurred_on,
    categoryId: row.category_id,
    incomeSourceId: row.income_source_id,
    refundOfTransactionId: row.refund_of_transaction_id,
    description: row.description,
    counterparty: row.counterparty,
    originalText: row.original_text,
    deletedAt: row.deleted_at,
    createdAt: row.created_at,
    entries: (row.transaction_entries ?? []).map((entry) => ({
      id: entry.id,
      accountId: entry.account_id,
      amountMinor: toMinor(entry.amount_minor),
    })),
  }));

  return { accounts, categories, incomeSources, transactions };
}

function operationParameters(draft: OperationDraft) {
  return {
    p_type: draft.type,
    p_amount_minor: draft.amountMinor.toString(),
    p_occurred_on: draft.occurredOn,
    p_account_id: draft.accountId,
    p_to_account_id: draft.toAccountId ?? null,
    p_direction: draft.direction ?? null,
    p_category_id: draft.categoryId ?? null,
    p_income_source_id: draft.incomeSourceId ?? null,
    p_refund_of_transaction_id: draft.refundOfTransactionId ?? null,
    p_description: draft.description ?? null,
    p_counterparty: draft.counterparty ?? null,
    p_original_text: draft.originalText ?? null,
  };
}

export async function createOperation(draft: OperationDraft): Promise<void> {
  assertOnline();
  const { error } = await requireSupabase().rpc("create_financial_transaction", {
    ...operationParameters(draft),
    p_client_operation_id: crypto.randomUUID(),
  });
  throwIfError(error);
}

export async function editOperation(transactionId: string, draft: OperationDraft): Promise<void> {
  assertOnline();
  const { error } = await requireSupabase().rpc("edit_financial_transaction", {
    p_transaction_id: transactionId,
    ...operationParameters(draft),
  });
  throwIfError(error);
}

export async function setTransactionDeleted(transactionId: string, deleted: boolean): Promise<void> {
  assertOnline();
  const { error } = await requireSupabase().rpc("set_financial_transaction_deleted", {
    p_transaction_id: transactionId,
    p_deleted: deleted,
  });
  throwIfError(error);
}

export async function createAccount(input: {
  name: string;
  kind: AccountKind;
  sortOrder: number;
  openingBalanceMinor: bigint;
  balanceKnownOn: string | null;
}): Promise<void> {
  assertOnline();
  const { error } = await requireSupabase().rpc("create_account_with_opening_balance", {
    p_name: input.name,
    p_kind: input.kind,
    p_sort_order: input.sortOrder,
    p_client_operation_id: crypto.randomUUID(),
    p_opening_balance_minor: input.openingBalanceMinor.toString(),
    p_balance_known_on: input.balanceKnownOn,
  });
  throwIfError(error);
}

export async function archiveAccount(accountId: string, archived: boolean): Promise<void> {
  assertOnline();
  const { error } = await requireSupabase().rpc("archive_account", {
    p_account_id: accountId,
    p_archived: archived,
  });
  throwIfError(error);
}

export async function updateAccountAndReconcileBalance(input: {
  accountId: string;
  name: string;
  organizationName: string | null;
  kind: AccountKind;
  accentColor: string | null;
  sortOrder: number;
  actualBalanceMinor: bigint;
  balanceKnownOn: string;
}): Promise<void> {
  assertOnline();
  const { error } = await requireSupabase().rpc("update_account_and_reconcile_balance", {
    p_account_id: input.accountId,
    p_name: input.name,
    p_organization_name: input.organizationName,
    p_kind: input.kind,
    p_accent_color: input.accentColor,
    p_sort_order: input.sortOrder,
    p_actual_balance_minor: input.actualBalanceMinor.toString(),
    p_balance_known_on: input.balanceKnownOn,
    p_client_operation_id: crypto.randomUUID(),
  });
  throwIfError(error);
}

export async function loadYearSummary(year: number): Promise<{
  months: MonthSummary[];
  incomeSources: IncomeSourceMonth[];
  expenseCategories: ExpenseCategoryYear[];
}> {
  const client = requireSupabase();
  const [monthResult, sourceResult, expenseResult] = await Promise.all([
    client.rpc("get_year_summary", { p_year: year }),
    client.rpc("get_year_income_source_matrix", { p_year: year }),
    client.rpc("get_year_expense_categories", { p_year: year }),
  ]);
  throwIfError(monthResult.error);
  throwIfError(sourceResult.error);
  throwIfError(expenseResult.error);

  return {
    months: (monthResult.data ?? []).map((row: Record<string, unknown>) => ({
      monthStart: String(row.month_start),
      incomeMinor: toMinor(row.income_minor as string),
      expenseMinor: toMinor(row.expense_minor as string),
      netMinor: toMinor(row.net_minor as string),
      origin: row.origin as MonthSummary["origin"],
    })),
    incomeSources: (sourceResult.data ?? []).map((row: Record<string, unknown>) => ({
      monthStart: String(row.month_start),
      incomeSourceId: row.income_source_id ? String(row.income_source_id) : null,
      name: String(row.name),
      amountMinor: toMinor(row.amount_minor as string),
      isUnclassified: Boolean(row.is_unclassified),
      origin: row.origin as IncomeSourceMonth["origin"],
    })),
    expenseCategories: (expenseResult.data ?? []).map((row: Record<string, unknown>) => ({
      categoryId: row.category_id ? String(row.category_id) : null,
      name: String(row.name),
      amountMinor: toMinor(row.amount_minor as string),
      isUnclassified: Boolean(row.is_unclassified),
    })),
  };
}

export async function createHistoricalPreview(rows: Array<Record<string, unknown>>) {
  assertOnline();
  const { data, error } = await requireSupabase().rpc("create_historical_import_preview", {
    p_client_operation_id: crypto.randomUUID(),
    p_rows: rows,
    p_original_filename: null,
    p_content_sha256: null,
  });
  throwIfError(error);
  return data as { id: string; total_items: number; valid_items: number; status: string };
}

export async function loadImportItems(batchId: string) {
  const { data, error } = await requireSupabase()
    .from("import_items")
    .select("id,row_number,status,raw_payload,normalized_payload,validation_errors")
    .eq("batch_id", batchId)
    .order("row_number");
  throwIfError(error);
  return data ?? [];
}

export async function applyHistoricalImport(batchId: string): Promise<void> {
  assertOnline();
  const { error } = await requireSupabase().rpc("apply_historical_import", { p_batch_id: batchId });
  throwIfError(error);
}
